import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { spaceApi } from '../services/api';
import { useSpaceId } from '../contexts/SpaceContext';
import type { CreateGatewayInput, UpdateGatewayInput } from '../types';

function useGatewayApi() {
  const spaceId = useSpaceId();
  return useMemo(() => spaceApi(spaceId).gateways, [spaceId]);
}

export function useGateways() {
  const spaceId = useSpaceId();
  const api = useGatewayApi();
  return useQuery({
    queryKey: ['space', spaceId, 'gateways'],
    queryFn: () => api.list(),
  });
}

export function useGateway(gatewayId: string | undefined) {
  const spaceId = useSpaceId();
  const api = useGatewayApi();
  return useQuery({
    queryKey: ['space', spaceId, 'gateways', gatewayId],
    queryFn: () => api.get(gatewayId!),
    enabled: !!gatewayId,
  });
}

export function useCreateGateway() {
  const spaceId = useSpaceId();
  const api = useGatewayApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateGatewayInput) => api.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['space', spaceId, 'gateways'] });
    },
  });
}

export function useUpdateGateway() {
  const spaceId = useSpaceId();
  const api = useGatewayApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ gatewayId, input }: { gatewayId: string; input: UpdateGatewayInput }) =>
      api.update(gatewayId, input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['space', spaceId, 'gateways'] });
      queryClient.invalidateQueries({
        queryKey: ['space', spaceId, 'gateways', variables.gatewayId],
      });
    },
  });
}

export function useDeleteGateway() {
  const spaceId = useSpaceId();
  const api = useGatewayApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (gatewayId: string) => api.delete(gatewayId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['space', spaceId, 'gateways'] });
    },
  });
}

export function useTestGateway() {
  const api = useGatewayApi();
  return useMutation({
    mutationFn: (gatewayId: string) => api.test(gatewayId),
  });
}

export function useGatewayTools(gatewayId: string | undefined) {
  const spaceId = useSpaceId();
  const api = useGatewayApi();
  return useQuery({
    queryKey: ['space', spaceId, 'gateways', gatewayId, 'tools'],
    queryFn: () => api.previewTools(gatewayId!),
    enabled: !!gatewayId,
    retry: false,
  });
}

export function useSyncGateway() {
  const spaceId = useSpaceId();
  const api = useGatewayApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (gatewayId: string) => api.sync(gatewayId),
    onSuccess: (_data, gatewayId) => {
      queryClient.invalidateQueries({ queryKey: ['space', spaceId, 'gateways', gatewayId] });
      queryClient.invalidateQueries({ queryKey: ['space', spaceId, 'gateways'] });
      queryClient.invalidateQueries({ queryKey: ['space', spaceId, 'tools'] });
    },
  });
}
